import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  Boxes, ArrowDownCircle, ArrowUpCircle, Flame, RefreshCcw, FileText, PlayCircle, StopCircle,
} from "lucide-react";
import MultiSelect from "../components/MultiSelect";

const TYPE_CLR = {
  sale: "bg-[#DCFCE7] text-[#15803D]",
  purchase: "bg-[#DBEAFE] text-[#1D4ED8]",
  transfer: "bg-[#F3E8FF] text-[#7E22CE]",
  refill: "bg-[#FFEDD5] text-[#9A3412]",
  adjustment: "bg-gray-100 text-gray-700",
  defective: "bg-[#FEE2E2] text-[#B91C1C]",
  opening: "bg-[#FEF3C7] text-[#B45309]",
};

export default function StockMovement() {
  const [godowns, setGodowns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [godown, setGodown] = useState([]);
  const [brand, setBrand] = useState([]);
  const [size, setSize] = useState([]);
  const [types, setTypes] = useState([]);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [g, b, s] = await Promise.all([api.get("/godowns"), api.get("/brands"), api.get("/sizes")]);
      const std = (g.data || []).filter((x) => x.kind !== "bullet");
      setGodowns(std); setBrands(b.data); setSizes(s.data);
      if (std[0]) setGodown([std[0].name]);
      if (b.data[0]) setBrand([b.data[0].name]);
      if (s.data[0]) setSize([s.data[0].name]);
    })().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const load = async () => {
    if (!godown.length || !brand.length || !size.length) return;
    setLoading(true);
    try {
      const { data } = await api.get("/stock/movement", {
        params: {
          godown: godown.join(","),
          brand: brand.join(","),
          size: size.join(","),
          types: types.length ? types.join(",") : undefined,
          date_from: from,
          date_to: to,
        },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [godown, brand, size, types, from, to]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const netFilled = (summary.filled_in || 0) - (summary.filled_out || 0);
  const netMt = (summary.mt_in || 0) - (summary.mt_out || 0);

  const label = useMemo(() => {
    if (!brand.length || !size.length) return "cylinders";
    const b = brand.length === 1 ? brand[0] : `${brand.length} brands`;
    const s = size.length === 1 ? size[0] : `${size.length} sizes`;
    return `${b} · ${s}`;
  }, [brand, size]);
  const godownLabel = godown.length === 1 ? godown[0] : `${godown.length} godowns`;

  return (
    <div className="space-y-6" data-testid="stock-movement-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Boxes size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Stock Movement</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Every filled / MT / refill in and out of a specific cylinder
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="movement-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      {/* Selectors */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <div>
          <label className="small-label block mb-2">Godown</label>
          <MultiSelect
            options={godowns.map((g) => ({ value: g.name, label: g.name }))}
            values={godown}
            onChange={setGodown}
            placeholder="Pick one or more godowns"
            testid="movement-godown" />
        </div>
        <div>
          <label className="small-label block mb-2">Brand</label>
          <MultiSelect
            options={brands.map((b) => ({ value: b.name, label: b.name }))}
            values={brand}
            onChange={setBrand}
            placeholder="Pick one or more brands"
            testid="movement-brand" />
        </div>
        <div>
          <label className="small-label block mb-2">Size (KG)</label>
          <MultiSelect
            options={sizes.map((s) => ({ value: s.name, label: s.name }))}
            values={size}
            onChange={setSize}
            placeholder="Pick one or more sizes"
            testid="movement-size" />
        </div>
        <div>
          <label className="small-label block mb-2">Movement type</label>
          <MultiSelect
            options={[
              { value: "sale", label: "Sale" },
              { value: "purchase", label: "Purchase" },
              { value: "transfer", label: "Transfer" },
              { value: "refill", label: "Refill" },
              { value: "opening", label: "Opening" },
              { value: "adjustment", label: "Adjustment" },
              { value: "defective", label: "Defective" },
            ]}
            values={types}
            onChange={setTypes}
            placeholder="All types"
            testid="movement-types" />
        </div>
        <div>
          <label className="small-label block mb-2">From</label>
          <input type="date" className="input-lg" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="movement-from" />
        </div>
        <div>
          <label className="small-label block mb-2">To</label>
          <input type="date" className="input-lg" value={to} onChange={(e) => setTo(e.target.value)} data-testid="movement-to" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70">Current Filled</div>
          <div className="data-num text-3xl mt-1" data-testid="movement-cur-filled">{data ? formatMoney(data.current_filled) : "—"}</div>
          <div className="text-xs text-white/70 mt-1">at {godownLabel || "—"}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Current MT</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="movement-cur-mt">{data ? formatMoney(data.current_mt) : "—"}</div>
        </div>
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#BBF7D0]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><ArrowDownCircle size={12} /> Filled IN</div>
          <div className="data-num text-2xl mt-1 text-[#15803D]">{summary.filled_in || 0}</div>
          <div className="text-xs text-[#15803D]/80 mt-1">purchase + refill + transfer-in</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><ArrowUpCircle size={12} /> Filled OUT</div>
          <div className="data-num text-2xl mt-1 text-[#B91C1C]">{summary.filled_out || 0}</div>
          <div className="text-xs text-[#B91C1C]/80 mt-1">sale + transfer-out + defective</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Net in period</div>
          <div className="data-num text-2xl mt-1">
            <span className={netFilled >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}>{netFilled >= 0 ? "+" : ""}{netFilled} F</span>
            <span className="text-gray-300 mx-2">·</span>
            <span className={netMt >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}>{netMt >= 0 ? "+" : ""}{netMt} MT</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">{summary.count || 0} movements</div>
        </div>
      </div>

      {/* Opening / Closing for the selected date window */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card-flat p-5 border-l-4 border-l-gray-800" data-testid="movement-opening-card">
          <div className="flex items-center gap-2">
            <PlayCircle size={16} className="text-gray-700" />
            <div className="small-label text-gray-500">Opening stock · as of {from}</div>
          </div>
          <div className="mt-2 flex items-baseline gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400">Filled</div>
              <div className="data-num text-3xl font-black" data-testid="movement-opening-filled">
                {data ? formatMoney(data.opening_filled) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400">MT</div>
              <div className="data-num text-3xl font-black text-[#B45309]" data-testid="movement-opening-mt">
                {data ? formatMoney(data.opening_mt) : "—"}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-gray-500 mt-2">Balance just before the first movement inside this window.</div>
        </div>
        <div className="card-flat p-5 border-l-4 border-l-[#15803D]" data-testid="movement-closing-card">
          <div className="flex items-center gap-2">
            <StopCircle size={16} className="text-[#15803D]" />
            <div className="small-label text-gray-500">Closing stock · as of {to}</div>
          </div>
          <div className="mt-2 flex items-baseline gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400">Filled</div>
              <div className="data-num text-3xl font-black" data-testid="movement-closing-filled">
                {data ? formatMoney(data.closing_filled) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400">MT</div>
              <div className="data-num text-3xl font-black text-[#B45309]" data-testid="movement-closing-mt">
                {data ? formatMoney(data.closing_mt) : "—"}
              </div>
            </div>
            {data && (
              <div className="ml-auto text-right">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">Δ vs opening</div>
                <div className="data-num text-sm font-bold">
                  <span className={data.closing_filled - data.opening_filled >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}>
                    {data.closing_filled - data.opening_filled >= 0 ? "+" : ""}{data.closing_filled - data.opening_filled} F
                  </span>
                  <span className="text-gray-300 mx-1.5">·</span>
                  <span className={data.closing_mt - data.opening_mt >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}>
                    {data.closing_mt - data.opening_mt >= 0 ? "+" : ""}{data.closing_mt - data.opening_mt} MT
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mt-2">Balance after the last movement inside this window.</div>
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <div className="p-4 border-b border-gray-200 flex items-center gap-2">
          <FileText size={14} className="text-gray-500" />
          <span className="small-label text-gray-500">
            {godown} · <span className="text-black font-bold">{label}</span> · {from} → {to}
          </span>
        </div>
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Type</th>
              <th className="text-left">Description</th>
              <th className="text-left">Challan</th>
              <th className="num">Filled Δ</th>
              <th className="num">MT Δ</th>
              <th className="num">Filled bal</th>
              <th className="num">MT bal</th>
              <th className="text-left">Vehicle</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="9" className="py-8 text-center text-sm text-gray-400">No movements for {label} in {godown} in this window.</td></tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={r.txn_id + "-" + i} data-testid={`movement-row-${r.txn_id}`}>
                <td className="data-num text-xs whitespace-nowrap">{r.date}</td>
                <td>
                  <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold ${TYPE_CLR[r.type] || "bg-gray-100"}`}>{r.type}</span>
                </td>
                <td className="text-xs">
                  {r.txn_id ? (
                    <Link to={`/challans`} className="hover:underline">{r.description}</Link>
                  ) : r.description}
                  {Array.isArray(r.items) && r.items.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`movement-items-${r.txn_id}`}>
                      {r.items.map((it, idx) => {
                        const fPart = it.filled_delta ? `${it.filled_delta > 0 ? "+" : ""}${it.filled_delta}F` : "";
                        const mPart = it.mt_delta ? `${it.mt_delta > 0 ? "+" : ""}${it.mt_delta}MT` : "";
                        const parts = [fPart, mPart].filter(Boolean).join(" · ");
                        return (
                          <span key={idx}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-gray-100 text-[10px] data-num text-gray-700">
                            <b className="text-black">{it.brand} {it.size}</b>
                            <span className="text-gray-400">{parts}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="data-num text-xs">{r.challan_no || <span className="text-gray-400">—</span>}</td>
                <td className={`num data-num font-bold ${r.filled_delta > 0 ? "text-[#15803D]" : r.filled_delta < 0 ? "text-[#B91C1C]" : "text-gray-400"}`}>
                  {r.filled_delta > 0 ? `+${r.filled_delta}` : r.filled_delta || ""}
                </td>
                <td className={`num data-num font-bold ${r.mt_delta > 0 ? "text-[#15803D]" : r.mt_delta < 0 ? "text-[#B91C1C]" : "text-gray-400"}`}>
                  {r.mt_delta > 0 ? `+${r.mt_delta}` : r.mt_delta || ""}
                </td>
                <td className="num font-bold">{r.filled_balance}</td>
                <td className="num font-bold text-[#B45309]">{r.mt_balance}</td>
                <td className="text-xs text-gray-500">
                  {r.vehicle_no || ""}
                  {r.driver_name && <div>{r.driver_name}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 leading-relaxed">
        Balances are running totals from beginning of time — the date range only trims which rows are displayed, not the arithmetic. So the Filled bal and MT bal of the top row equal the current stock as of that day.
      </p>

      <Flame size={0} />
    </div>
  );
}
